import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faPhone, faEnvelope, faChevronDown, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { faInstagram, faFacebook } from "@fortawesome/free-brands-svg-icons";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";

import AkasbLogo from "../assets/img/logo_akasb.png";
import HeroBackground from "../assets/img/akasb-background.jpg";
import fundacao2023Img from "../assets/img/akasb-historia.jpg";
import ITKF2024Img from "../assets/img/ITKF2024.jpeg";
import GilAkasb2025Img from "../assets/img/GilAkasb2025.jpeg";
import DojoInauguracao2026Img from "../assets/img/DojoInauguracao2026.jpeg";
import instrutorBruna from "../assets/img/instrutor2.jpg";
import instrutorAntonio from "../assets/img/instrutor1.jpg";
import instrutorJorge from "../assets/img/instrutor3.jpg";
import instrutorTiago from "../assets/img/instrutor4.jpeg";

const INSTRUCTORS = [
  { img: instrutorJorge,   name: "Jorge Perestrelo", role: "Diretor Técnico", dan: "3º Dan" },
  { img: instrutorAntonio, name: "António Cardoso",  role: "Treinador",        dan: "1º Dan" },
  { img: instrutorBruna,   name: "Bruna Gonçalves",  role: "Treinadora",       dan: "1º Dan" },
  { img: instrutorTiago,   name: "Tiago Ribeiro",    role: "Treinador",        dan: "1º Dan" },
];

const SCHEDULE = [
  { day: "Segunda", short: "SEG",
    classes: [
      { time: "18:30–19:15", name: "Iniciação",        detail: "Cintos Brancos e Amarelos - até aos 8 anos", color: "#E4B428" },
      { time: "19:15–20:00", name: "Shotokan Tradicional", detail: "Todas as Idades", color: "#4f8ef7" },
      { time: "20:00–21:00", name: "Competição Kumite/Kata", detail: "Todas as Idades", color: "#e05252" },
    ]},
  { day: "Terça", short: "TER",
    classes: [
      { time: "18:30–19:15", name: "Competição Kata",   detail: "Todas as Idades", color: "#8b5cf6" },
      { time: "19:15–20:00", name: "Competição Kumite",  detail: "Todas as Idades", color: "#e05252" },
      null,
    ]},
  { day: "Quarta",  short: "QUA",
    classes: [
      { time: "18:30–19:15", name: "Iniciação",        detail: "Cintos Brancos e Amarelos - até aos 8 anos", color: "#E4B428" },
      { time: "19:15–20:00", name: "Shotokan Tradicional", detail: "Todas as Idades", color: "#4f8ef7" },
      { time: "20:00–21:00", name: "Competição Kumite/Kata", detail: "Todas as Idades", color: "#e05252" },
    ]},
  { day: "Sexta",   short: "SEX",
    classes: [
      { time: "18:30–19:15", name: "Iniciação",        detail: "Cintos Brancos e Amarelos - até aos 8 anos", color: "#E4B428" },
      { time: "19:15–20:00", name: "Shotokan Tradicional", detail: "Todas as Idades", color: "#4f8ef7" },
      { time: "20:00–21:30", name: "Competição Kumite/Kata", detail: "Todas as Idades", color: "#e05252" },
    ]},
];

function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function Reveal({ children, delay = 0, from = "bottom", style = {} }) {
  const [ref, inView] = useInView();
  const transforms = { bottom: "translateY(50px)", left: "translateX(-50px)", right: "translateX(50px)" };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "none" : transforms[from],
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

export default function Presentation() {
  const [scrollY, setScrollY] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const heroRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navScrolled = scrollY > 60;

  const NAV_LINKS = [
    ["#historia", "O Clube"], ["#instrutores", "Equipa"], ["#horario", "Horários"],
    ["#localizacao", "Localização"], ["#contactos", "Contactos"],
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Bebas+Neue&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,600;1,9..40,300&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; font-size: 16px; }
        body { font-family: 'DM Sans', sans-serif; background: #fff; overflow-x: hidden; color: #1a1830; }
        img { display: block; max-width: 100%; }
        a { text-decoration: none; color: inherit; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #0d0b1e; }
        ::-webkit-scrollbar-thumb { background: #E4B428; border-radius: 2px; }

        /* ── Animations ── */
        @keyframes fadeSlideUp   { from { opacity:0; transform:translateY(60px);  } to { opacity:1; transform:none; } }
        @keyframes fadeSlideLeft { from { opacity:0; transform:translateX(80px);  } to { opacity:1; transform:none; } }
        @keyframes scaleIn       { from { opacity:0; transform:scale(0.85);        } to { opacity:1; transform:none; } }
        @keyframes floatY        { 0%,100% { transform:translateY(0);  } 50% { transform:translateY(-10px); } }
        @keyframes shimmer       { 0%,100% { opacity:.6; } 50% { opacity:1; } }
        @keyframes rotateSlow    { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        @keyframes marquee       { from { transform:translateX(0); } to { transform:translateX(-50%); } }
        @keyframes barGrow       { from { transform:scaleX(0); transform-origin:left; } to { transform:scaleX(1); } }
        @keyframes pulseGlow     { 0%,100% { box-shadow:0 0 0 0 rgba(228,180,40,.4); } 70% { box-shadow:0 0 0 12px rgba(228,180,40,0); } }

        .anim-hero-badge  { animation: fadeSlideUp   .9s cubic-bezier(.16,1,.3,1) .2s both; }
        .anim-hero-title  { animation: fadeSlideLeft 1s cubic-bezier(.16,1,.3,1) .45s both; }
        .anim-hero-sub    { animation: fadeSlideUp   .9s cubic-bezier(.16,1,.3,1) .7s both; }
        .anim-hero-btn    { animation: scaleIn        .7s cubic-bezier(.16,1,.3,1) .95s both; }
        .anim-float       { animation: floatY 4s ease-in-out infinite; }
        .anim-shimmer     { animation: shimmer 3s ease-in-out infinite; }

        /* ── Navbar ── */
        .nav-pill { color: rgba(255,255,255,.7); font-size:12px; font-weight:600; letter-spacing:.8px;
          text-transform:uppercase; padding:7px 16px; border-radius:30px; transition:all .2s; white-space:nowrap; }
        .nav-pill:hover { color:#fff; background:rgba(255,255,255,.1); }

        /* ── Instructor cards ── */
        .inst-card { position:relative; overflow:hidden; border-radius:16px; cursor:default;
          transition:transform .4s cubic-bezier(.16,1,.3,1), box-shadow .4s ease; }
        .inst-card:hover { transform:translateY(-8px); box-shadow:0 28px 60px rgba(0,0,0,.35) !important; }
        .inst-card:hover .inst-img { transform:scale(1.07); }
        .inst-card:hover .inst-overlay { opacity:1; }
        .inst-img { width:100%; height:100%; object-fit:cover; object-position:center 15%; transition:transform .5s cubic-bezier(.16,1,.3,1); }
        .inst-overlay { position:absolute; inset:0; background:linear-gradient(to top, rgba(13,11,30,.95) 35%, transparent 70%);
          transition:opacity .3s; }

        /* ── Schedule cells ── */
        .sched-cell { transition:transform .25s ease, box-shadow .25s ease; cursor:default; }
        .sched-cell:hover { transform:translateY(-4px); box-shadow:0 12px 32px rgba(0,0,0,.12) !important; }

        /* ── Contact cards ── */
        .contact-card { transition:transform .3s ease, box-shadow .3s ease; }
        .contact-card:hover { transform:translateY(-5px); }

        /* ── Ticker ── */
        .ticker-inner { display:flex; width:max-content; animation:marquee 40s linear infinite; }
        .ticker-inner:hover { animation-play-state:paused; }

        /* ── Hero banner responsive sizing ── */
        .hero-section { background-size: cover; background-position: center top; background-attachment: scroll; }
        @media (min-width:901px) {
          .hero-section { background-size: 100%; background-position: center top; background-attachment: fixed; }
        }
        @media (max-width:900px) {
          .hero-section { background-size: cover; background-position: 30% top; background-attachment: scroll; }
        }

        /* ── Mobile ── */
        @media (max-width:900px) {
          .hide-mobile { display:none !important; }
          .schedule-grid { grid-template-columns:1fr !important; }
          .schedule-day-col { flex-direction:row !important; writing-mode:horizontal-tb !important;
            transform:none !important; border-radius:10px !important; padding:10px 16px !important;
            align-items:center !important; justify-content:flex-start !important; gap:8px; }
          /* Compact schedule/calendar on mobile */
          .schedule-row { grid-template-columns: 52px 1fr 1fr 1fr !important; gap: 6px !important; }
          .schedule-day-col span { font-size: 11px !important; }
          .sched-cell { padding: 8px 6px !important; }
          .sched-cell > div:first-child { font-size: 9px !important; margin-bottom: 3px !important; }
          .sched-cell > div:nth-child(2) { font-size: 11px !important; margin-bottom: 2px !important; }
          .sched-cell > div:nth-child(3) { font-size: 9px !important; }

          /* Milestone photos — pull back in so right edge isn't clipped, and ease the overlap */
          .milestone-2024 { margin-left: 26% !important; width: 62% !important; margin-top: -60px !important; }
          .milestone-2025 { margin-top: -75px !important; }
          .milestone-2026 { margin-left: 16% !important; width: 72% !important; margin-top: -65px !important; }
        }
        @media (max-width:600px) {
          .hero-logo-img { height:48px !important; }
        }
      `}</style>

      {/* ═══════════════════════════════════════════
          NAVBAR
      ═══════════════════════════════════════════ */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        padding: navScrolled ? "10px 32px" : "22px 32px",
        background: navScrolled ? "rgba(13,11,30,.96)" : "transparent",
        backdropFilter: navScrolled ? "blur(16px)" : "none",
        boxShadow: navScrolled ? "0 1px 0 rgba(255,255,255,.06)" : "none",
        transition: "all .4s cubic-bezier(.16,1,.3,1)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <HashLink to="#home" style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <img src={AkasbLogo} alt="AKASB" style={{ height: 34, filter: "drop-shadow(0 2px 8px rgba(228,180,40,.3))" }} />
          <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 22, color: "#fff", letterSpacing: 3 }}>AKASB</span>
        </HashLink>

        {/* Desktop nav */}
        <div className="hide-mobile" style={{ display: "flex", alignItems: "center", gap: 2 }}>
          {NAV_LINKS.map(([href, label]) => (
            <HashLink key={href} to={href} className="nav-pill">{label}</HashLink>
          ))}
        </div>

        <div className="hide-mobile">
          <Link to="/connect" style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "9px 22px", background: "#E4B428", color: "#0d0b1e",
            borderRadius: 30, fontWeight: 700, fontSize: 12, letterSpacing: 1,
            textTransform: "uppercase", transition: "all .2s",
            boxShadow: "0 4px 20px rgba(228,180,40,.35)",
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.04)"; e.currentTarget.style.boxShadow = "0 6px 28px rgba(228,180,40,.5)"; }}
          onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 4px 20px rgba(228,180,40,.35)"; }}>
            AKASB Connect <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 10 }} />
          </Link>
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setMenuOpen(v => !v)} style={{
          display: "none", background: "rgba(255,255,255,.1)", border: "none",
          borderRadius: 8, width: 40, height: 40, cursor: "pointer",
          flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5,
        }} className="show-mobile-flex"
        aria-label="Menu">
          {[0,1,2].map(i => (
            <span key={i} style={{
              display: "block", width: 20, height: 2, background: "#fff", borderRadius: 1, transition: "all .3s",
              transform: menuOpen
                ? i === 0 ? "rotate(45deg) translateY(7px)" : i === 2 ? "rotate(-45deg) translateY(-7px)" : "scaleX(0)"
                : "none",
              opacity: menuOpen && i === 1 ? 0 : 1,
            }} />
          ))}
        </button>
      </nav>

      {/* Mobile full-screen menu */}
      <div style={{
        position: "fixed", inset: 0, zIndex: 999,
        background: "#0d0b1e",
        transform: menuOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform .4s cubic-bezier(.16,1,.3,1)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
        padding: "40px 32px",
      }}>
        {/* Decorative */}
        <div style={{ position: "absolute", top: "15%", right: "10%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(228,180,40,.08) 0%, transparent 70%)" }} />
        {NAV_LINKS.map(([href, label], i) => (
          <HashLink key={href} to={href} onClick={() => setMenuOpen(false)} style={{
            fontFamily: "'Anton', sans-serif", fontSize: "clamp(2rem, 8vw, 3rem)",
            color: "rgba(255,255,255,.85)", letterSpacing: 3, textTransform: "uppercase",
            padding: "10px 0", lineHeight: 1.1, transition: "color .2s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = "#E4B428"}
          onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,.85)"}>
            {label}
          </HashLink>
        ))}
        <div style={{ width: 48, height: 2, background: "rgba(255,255,255,.12)", margin: "20px 0" }} />
        <Link to="/connect" onClick={() => setMenuOpen(false)} style={{
          padding: "16px 48px", background: "#E4B428", color: "#0d0b1e",
          borderRadius: 30, fontWeight: 800, fontSize: 16, letterSpacing: 1,
          textTransform: "uppercase", boxShadow: "0 8px 32px rgba(228,180,40,.4)",
        }}>
          AKASB Connect
        </Link>
      </div>

      <main>
        {/* ═══════════════════════════════════════════
            HERO
        ═══════════════════════════════════════════ */}
        <section id="home" ref={heroRef} className="hero-section" style={{
          position: "relative", minHeight: "100svh",
          backgroundImage: `url(${HeroBackground})`,
          backgroundRepeat: "no-repeat",
          backgroundColor: "#0d0b1e",
          display: "flex", alignItems: "flex-end",
          overflow: "hidden",
        }}>
          {/* Gradient layers */}
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(120deg, rgba(13,11,30,.9) 0%, rgba(13,11,30,.55) 55%, rgba(0,0,0,.3) 100%)", zIndex: 1 }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "50%", background: "linear-gradient(to top, rgba(13,11,30,1) 0%, transparent 100%)", zIndex: 1 }} />

          {/* Geometric accent — diagonal gold bar */}
          <div style={{
            position: "absolute", top: 0, right: "18%", width: 3, height: "60%",
            background: "linear-gradient(to bottom, transparent, #E4B428, transparent)",
            zIndex: 2, opacity: .4,
          }} />
          <div style={{
            position: "absolute", top: "8%", right: "calc(18% + 12px)", width: 1, height: "30%",
            background: "linear-gradient(to bottom, transparent, rgba(228,180,40,.4), transparent)",
            zIndex: 2,
          }} />

          {/* Rotating ring — desktop decorative */}
          <div className="hide-mobile anim-shimmer" style={{
            position: "absolute", right: "8%", top: "20%", zIndex: 2,
            width: 280, height: 280,
            border: "1px solid rgba(228,180,40,.15)",
            borderRadius: "50%",
          }} />
          <div className="hide-mobile" style={{
            position: "absolute", right: "10%", top: "22%", zIndex: 2,
            width: 240, height: 240,
            border: "1px solid rgba(228,180,40,.08)",
            borderRadius: "50%",
            animation: "rotateSlow 30s linear infinite",
          }}>
            {/* Dot on ring */}
            <div style={{ position: "absolute", top: -4, left: "50%", width: 8, height: 8, borderRadius: "50%", background: "#E4B428", transform: "translateX(-50%)", boxShadow: "0 0 12px #E4B428" }} />
          </div>

          {/* Content */}
          <div style={{ position: "relative", zIndex: 3, width: "100%", padding: "0 7vw clamp(60px, 12vh, 100px)" }}>
            {/* Badge */}
            <div className="anim-hero-badge" style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 24 }}>
              <div style={{ width: 24, height: 1, background: "#E4B428" }} />
              <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>
                Associação Karate Shotokan de Barcelos
              </span>
              <div style={{ width: 24, height: 1, background: "#E4B428" }} />
            </div>

            {/* Logo + title side by side */}
            <div className="anim-hero-title" style={{ display: "flex", alignItems: "center", gap: "clamp(16px, 4vw, 40px)", marginBottom: 8, flexWrap: "wrap" }}>
              <img src={AkasbLogo} alt="AKASB Logo" className="hero-logo-img" style={{
                height: "clamp(52px, 11vw, 96px)",
                filter: "drop-shadow(0 6px 24px rgba(228,180,40,.5)) drop-shadow(0 2px 8px rgba(0,0,0,.4))",
              }} />
              <h1 style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: "clamp(4.5rem, 18vw, 11rem)",
                color: "#fff",
                lineHeight: .88,
                letterSpacing: "clamp(2px, 1vw, 8px)",
                textShadow: "0 8px 60px rgba(0,0,0,.4)",
              }}>AKASB</h1>
            </div>

            {/* Subtitle */}
            <p className="anim-hero-sub" style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontSize: "clamp(1.3rem, 5vw, 2.6rem)",
              color: "rgba(255,255,255,.75)",
              letterSpacing: "clamp(4px, 1.5vw, 10px)",
              marginBottom: "clamp(32px, 6vh, 52px)",
              textTransform: "uppercase",
            }}>
              Vem Praticar Karate
            </p>

            {/* CTA */}
            <div className="anim-hero-btn" style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
              <Link to="/connect" style={{
                display: "inline-flex", alignItems: "center", gap: 10,
                padding: "clamp(12px, 2vw, 16px) clamp(24px, 4vw, 40px)",
                background: "#E4B428", color: "#0d0b1e",
                borderRadius: 3, fontWeight: 800,
                fontSize: "clamp(12px, 2vw, 14px)",
                letterSpacing: 2, textTransform: "uppercase",
                boxShadow: "0 8px 40px rgba(228,180,40,.45)",
                transition: "all .25s",
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 14px 48px rgba(228,180,40,.6)"; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 8px 40px rgba(228,180,40,.45)"; }}>
                Inscreve-te Já <FontAwesomeIcon icon={faArrowRight} />
              </Link>
              <HashLink to="#historia" style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "clamp(12px, 2vw, 16px) clamp(24px, 4vw, 36px)",
                color: "rgba(255,255,255,.8)", borderRadius: 3, fontWeight: 600,
                fontSize: "clamp(12px, 2vw, 14px)",
                letterSpacing: 2, textTransform: "uppercase",
                border: "1.5px solid rgba(255,255,255,.25)", transition: "all .25s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.5)"; e.currentTarget.style.color = "#fff"; }}
              onMouseLeave={e => { e.currentTarget.style.background = ""; e.currentTarget.style.borderColor = "rgba(255,255,255,.25)"; e.currentTarget.style.color = "rgba(255,255,255,.8)"; }}>
                Saber Mais
              </HashLink>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            MARQUEE TICKER
        ═══════════════════════════════════════════ */}
        <div style={{ background: "#E4B428", padding: "14px 0", overflow: "hidden", borderTop: "1px solid rgba(255,255,255,.1)" }}>
          <div className="ticker-inner">
            {Array(8).fill(["KARATE SHOTOKAN", "KATA", "KUMITE", "BARCELOS", "DISCIPLINA", "RESPEITO", "EQUIPA", "CAMPEÕES"]).flat().map((word, i) => (
              <span key={i} style={{
                fontFamily: "'Anton', sans-serif", fontSize: 13,
                color: "#0d0b1e", letterSpacing: 3, textTransform: "uppercase",
                padding: "0 24px",
              }}>
                {word} <span style={{ color: "rgba(13,11,30,.3)", margin: "0 8px" }}>◆</span>
              </span>
            ))}
          </div>
        </div>

        {/* ═══════════════════════════════════════════
            O NOSSO CLUBE
        ═══════════════════════════════════════════ */}
        <section id="historia" style={{ padding: "clamp(72px, 12vh, 120px) 7vw", background: "#fff", overflow: "hidden" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 460px), 1fr))", gap: "clamp(32px, 6vw, 70px)", alignItems: "start" }}>

            {/* Photo collage / milestones wall — staircase pattern */}
            <Reveal from="left">
              <div style={{ position: "relative", maxWidth: 420, margin: "0 auto", paddingBottom: 28 }}>
                {[
                  { year: "2023", label: "Fundação",                     img: fundacao2023Img, width: "77%", ml: "0%",  mt: 0,    rotate: -3, badgeCorner: "right", ratio: "4/3" },
                  { year: "2024", label: "22º Campeonato do Mundo ITKF", img: ITKF2024Img, width: "67%", ml: "38%", mt: -140, rotate: 3,  badgeCorner: "right", ratio: "3/4" },
                  { year: "2025", label: "Gil Vicente FC/AKASB",         img: GilAkasb2025Img, width: "70%", ml: "0%",  mt: -175, rotate: -2, badgeCorner: "left",  ratio: "3/4" },
                  { year: "2026", label: "Dojo Cidade de Barcelos",      img: DojoInauguracao2026Img, width: "79%", ml: "35%", mt: -160, rotate: 2,  badgeCorner: "right", ratio: "4/3" },
                ].map((m, i) => (
                  <div key={m.year} className={`milestone-${m.year}`} style={{
                    marginLeft: m.ml, marginTop: m.mt, position: "relative", zIndex: i + 1,
                    width: m.width,
                  }}>
                    <div style={{
                      transform: `rotate(${m.rotate}deg)`,
                      position: "relative",
                    }}>
                      <img src={m.img} alt={m.label} style={{
                        width: "100%", aspectRatio: m.ratio, objectFit: "cover",
                        borderRadius: 6, border: "6px solid #fff",
                        boxShadow: "0 20px 50px rgba(0,0,0,.22)",
                        display: "block",
                      }} />
                      {/* Year + label badge on corner */}
                      <div style={{
                        position: "absolute", top: -12, [m.badgeCorner]: -12,
                        background: "#E4B428", borderRadius: 8, padding: "7px 12px",
                        textAlign: "center", boxShadow: "0 6px 18px rgba(228,180,40,.45)",
                        transform: `rotate(${-m.rotate}deg)`,
                        zIndex: 10, maxWidth: 148,
                      }}>
                        <div style={{ fontFamily: "'Anton', sans-serif", fontSize: 16, color: "#0d0b1e", lineHeight: 1, marginBottom: 2 }}>{m.year}</div>
                        <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(13,11,30,.7)", letterSpacing: .3, lineHeight: 1.25, whiteSpace: "normal" }}>{m.label}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Text block */}
            <div>
              <Reveal delay={0.1}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 3, background: "#E4B428", borderRadius: 2, animation: "barGrow 1s ease both" }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Sobre Nós</span>
                </div>
              </Reveal>
              <Reveal delay={0.18}>
                <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.4rem, 6vw, 4rem)", color: "#0d0b1e", lineHeight: .92, textTransform: "uppercase", marginBottom: 28 }}>
                  O Nosso<br />Clube
                </h2>
              </Reveal>
              <Reveal delay={0.26}>
                <p style={{ fontSize: 15, color: "#5a5870", lineHeight: 1.9, marginBottom: 18 }}>
                  O Karate Shotokan nasceu no Japão pelas mãos de Gichin Funakoshi, que no início do século XX levou esta arte marcial de Okinawa até ao Japão continental, moldando-a numa via de disciplina física e espiritual. É hoje um dos estilos de karate mais praticados no mundo, reconhecido pela sua base técnica sólida, posições fortes e enorme rigor formativo.
                </p>
                <p style={{ fontSize: 15, color: "#5a5870", lineHeight: 1.9, marginBottom: 18 }}>
                  A AKASB — Associação Karate Shotokan de Barcelos foi fundada em 2023 com a missão de promover esta tradição como disciplina desportiva, educativa e cultural, formando atletas de excelência e cidadãos de carácter.
                </p>
                <p style={{ fontSize: 15, color: "#5a5870", lineHeight: 1.9, marginBottom: 18 }}>
                  Em parceria com o Gil Vicente Futebol Clube, demos origem à secção <strong style={{ color: "#0d0b1e" }}>Gil Vicente FC/AKASB</strong>, unindo a força e a história de um clube centenário à identidade técnica e formativa da nossa associação.
                </p>
                <p style={{ fontSize: 15, color: "#5a5870", lineHeight: 1.9, marginBottom: 36 }}>
                  Estamos filiados na Federação Nacional de Karate de Portugal (FNKP) e integramos o Centro Português de Karate (CPK), o que garante aos nossos atletas formação certificada, participação em competições oficiais e um percurso reconhecido a nível nacional.
                </p>
              </Reveal>
              {/* Stats row */}
              <Reveal delay={0.34}>
                <div style={{ display: "flex", gap: "clamp(20px, 5vw, 48px)", flexWrap: "wrap" }}>
                  {[["2023", "Fundação"], ["+90", "Atletas"], ["4", "Instrutores"]].map(([val, label]) => (
                    <div key={label} style={{ position: "relative" }}>
                      <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2rem, 5vw, 3rem)", color: "#0d0b1e", lineHeight: 1 }}>{val}</div>
                      <div style={{ fontSize: 11, color: "#a0a0b8", letterSpacing: 2, textTransform: "uppercase", marginTop: 4 }}>{label}</div>
                      <div style={{ position: "absolute", bottom: -8, left: 0, width: "60%", height: 2, background: "#E4B428", borderRadius: 1 }} />
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            EQUIPA TÉCNICA
        ═══════════════════════════════════════════ */}
        <section id="instrutores" style={{ padding: "clamp(72px, 12vh, 120px) 7vw", background: "#0d0b1e", overflow: "hidden", position: "relative" }}>
          {/* Background decoration */}
          <div style={{ position: "absolute", top: "10%", left: "-5%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(228,180,40,.05) 0%, transparent 70%)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: "5%", right: "0%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,142,247,.05) 0%, transparent 70%)", pointerEvents: "none" }} />

          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            {/* Section header */}
            <Reveal>
              <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 24, marginBottom: 64, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                    <div style={{ width: 40, height: 3, background: "#E4B428", borderRadius: 2 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Os Nossos Instrutores</span>
                  </div>
                  <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.4rem, 6vw, 4rem)", color: "#fff", lineHeight: .92, textTransform: "uppercase" }}>
                    Equipa<br />Técnica
                  </h2>
                </div>
                <p style={{ fontSize: 14, color: "rgba(255,255,255,.4)", maxWidth: 320, lineHeight: 1.8 }}>
                  Conheça os responsáveis pela sua evolução no tatami — experientes, dedicados e apaixonados pelo karate.
                </p>
              </div>
            </Reveal>

            {/* Cards */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 20 }}>
              {INSTRUCTORS.map((inst, i) => (
                <Reveal key={inst.name} delay={i * 0.1}>
                  <div className="inst-card" style={{ height: 360, boxShadow: "0 8px 32px rgba(0,0,0,.3)" }}>
                    <img src={inst.img} alt={inst.name} className="inst-img" style={{ position: "absolute", inset: 0, height: "100%", objectFit: "cover", objectPosition: "center 15%" }} />
                    <div className="inst-overlay" />
                    {/* Dan badge — top right */}
                    <div style={{
                      position: "absolute", top: 14, right: 14, zIndex: 2,
                      background: "rgba(228,180,40,.9)", backdropFilter: "blur(8px)",
                      borderRadius: 20, padding: "4px 12px",
                      fontSize: 11, fontWeight: 800, color: "#0d0b1e", letterSpacing: 1,
                    }}>
                      {inst.dan}
                    </div>
                    {/* Info — bottom */}
                    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 2, padding: "20px 20px 22px" }}>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,.5)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>{inst.role}</div>
                      <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: 17, color: "#fff" }}>{inst.name}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            HORÁRIOS
        ═══════════════════════════════════════════ */}
        <section id="horario" style={{ padding: "clamp(72px, 12vh, 120px) 7vw", background: "#f7f7fb", overflow: "hidden" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto" }}>
            <Reveal>
              <div style={{ marginBottom: 56 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                  <div style={{ width: 40, height: 3, background: "#E4B428", borderRadius: 2 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Semana de Treinos</span>
                </div>
                <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.4rem, 6vw, 4rem)", color: "#0d0b1e", lineHeight: .92, textTransform: "uppercase" }}>
                  Horários<br />dos Treinos
                </h2>
              </div>
            </Reveal>

            <Reveal delay={0.15}>
              <div className="schedule-grid" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {SCHEDULE.map((row) => (
                  <div key={row.day} className="schedule-row" style={{ display: "grid", gridTemplateColumns: "72px 1fr 1fr 1fr", gap: 12, alignItems: "stretch" }}>
                    {/* Day column */}
                    <div className="schedule-day-col" style={{
                      background: "#0d0b1e", borderRadius: 12,
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                      padding: "14px 0",
                    }}>
                      <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 13, color: "#fff", letterSpacing: 2, writingMode: "vertical-rl", transform: "rotate(180deg)", textTransform: "uppercase" }}>{row.day}</span>
                    </div>
                    {/* Class cells */}
                    {row.classes.map((cls, j) => cls ? (
                      <div key={j} className="sched-cell" style={{
                        background: "#fff", borderRadius: 12,
                        padding: "clamp(14px, 2vw, 20px) clamp(12px, 2vw, 18px)",
                        borderTop: `3px solid ${cls.color}`,
                        boxShadow: "0 2px 16px rgba(0,0,0,.06)",
                      }}>
                        <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6, fontWeight: 600 }}>{cls.time}</div>
                        <div style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(13px, 2vw, 16px)", color: "#0d0b1e", marginBottom: 4 }}>{cls.name}</div>
                        <div style={{ fontSize: 12, color: cls.color, fontWeight: 700, letterSpacing: .5 }}>{cls.detail}</div>
                      </div>
                    ) : (
                      <div key={j} style={{ background: "rgba(0,0,0,.025)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ color: "#d1d5db", fontSize: 24, fontWeight: 200 }}>–</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Legend */}
              <div style={{ display: "flex", gap: "clamp(12px, 3vw, 28px)", marginTop: 28, flexWrap: "wrap" }}>
                {[["#E4B428","Iniciação (Até 8 Anos · Cintos Brancos e Amarelos)"],["#4f8ef7","Shotokan Tradicional (Todas as Idades)"],["#8b5cf6","Competição Kata (Todas as Idades)"],["#e05252","Competição Kumite / Kumite+Kata (Todas as Idades)"]].map(([c, l]) => (
                  <div key={l} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#6b6b88" }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: c, flexShrink: 0 }} />
                    {l}
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            LOCALIZAÇÃO
        ═══════════════════════════════════════════ */}
        <section id="localizacao" style={{ padding: "clamp(72px, 12vh, 120px) 7vw", background: "#fff", overflow: "hidden" }}>
          <div style={{ maxWidth: 1160, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 400px), 1fr))", gap: "clamp(40px, 8vw, 80px)", alignItems: "center" }}>
            <div>
              <Reveal>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                  <div style={{ width: 40, height: 3, background: "#E4B428", borderRadius: 2 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Onde Estamos</span>
                </div>
                <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.4rem, 6vw, 4rem)", color: "#0d0b1e", lineHeight: .92, textTransform: "uppercase", marginBottom: 24 }}>
                  A Nossa<br />Localização
                </h2>
                <p style={{ fontSize: 15, color: "#5a5870", lineHeight: 1.9, marginBottom: 24 }}>
                  Os nossos treinos decorrem num espaço amplo e equipado para a prática segura e eficaz do karate, em Arcozelo, Barcelos.
                </p>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 32, fontSize: 14, color: "#5a5870" }}>
                  <FontAwesomeIcon icon={faMapMarkerAlt} style={{ color: "#E4B428" }} />
                  R. Henrique Correia, Arcozelo, Barcelos
                </div>
                <a href="https://maps.app.goo.gl/ruHerUPseAj3ouEp6" target="_blank" rel="noreferrer" style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "13px 30px", background: "#0d0b1e", color: "#fff",
                  borderRadius: 3, fontWeight: 700, fontSize: 12,
                  letterSpacing: 2, textTransform: "uppercase",
                  transition: "all .2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "#E4B428"; e.currentTarget.style.color = "#0d0b1e"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#0d0b1e"; e.currentTarget.style.color = "#fff"; }}>
                  Ver no Mapa <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 11 }} />
                </a>
              </Reveal>
            </div>

            <Reveal delay={0.15} from="right">
              <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 24px 80px rgba(0,0,0,.18)", position: "relative" }}>
                {/* Corner accent */}
                <div style={{ position: "absolute", top: 0, left: 0, width: 60, height: 4, background: "#E4B428", zIndex: 1 }} />
                <div style={{ position: "absolute", top: 0, left: 0, width: 4, height: 60, background: "#E4B428", zIndex: 1 }} />
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d487.8!2d-8.6090295!3d41.5431951!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd2453403a176783%3A0x1a877d8b6fb94f11!2sAKASB%20-%20Associa%C3%A7%C3%A3o%20Karate%20Shotokan%20de%20Barcelos!5e0!3m2!1spt!2spt!4v1"
                  height="440" title="Localização AKASB"
                  style={{ border: 0, width: "100%", display: "block" }}
                  allowFullScreen loading="lazy"
                />
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            CONTACTOS
        ═══════════════════════════════════════════ */}
        <section id="contactos" style={{ padding: "clamp(72px, 12vh, 120px) 7vw", background: "#0d0b1e", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle, rgba(228,180,40,.04) 0%, transparent 70%)", pointerEvents: "none" }} />

          <div style={{ maxWidth: 960, margin: "0 auto" }}>
            <Reveal>
              <div style={{ textAlign: "center", marginBottom: 64 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginBottom: 16 }}>
                  <div style={{ width: 30, height: 2, background: "#E4B428" }} />
                  <span style={{ fontSize: 11, fontWeight: 700, color: "#E4B428", letterSpacing: 3, textTransform: "uppercase" }}>Fale Connosco</span>
                  <div style={{ width: 30, height: 2, background: "#E4B428" }} />
                </div>
                <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2.4rem, 6vw, 4rem)", color: "#fff", lineHeight: .92, textTransform: "uppercase" }}>
                  Contactos
                </h2>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16 }}>
                {[
                  { icon: faPhone,     label: "Telefone",  value: "(+351) 938 052 610",  href: "tel:+351938052610",          color: "#4f8ef7" },
                  { icon: faEnvelope,  label: "Email",     value: "geral@akasb.pt",       href: "mailto:geral@akasb.pt",      color: "#E4B428" },
                  { icon: faInstagram, label: "Instagram", value: "@akasb_karatebarcelos",        href: "https://www.instagram.com/akasb_karatebarcelos/",                          color: "#e05252" },
                  { icon: faFacebook,  label: "Facebook",  value: "AKASB",                href: "https://www.facebook.com/akasbkaratebarcelos/",                          color: "#4f8ef7" },
                ].map(({ icon, label, value, href, color }) => (
                  <a key={label} href={href} className="contact-card" style={{
                    background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.08)",
                    borderRadius: 16, padding: "clamp(20px, 3vw, 32px) 20px",
                    display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                    boxShadow: "0 4px 24px rgba(0,0,0,.2)",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; e.currentTarget.style.borderColor = `${color}40`; e.currentTarget.style.boxShadow = `0 12px 40px rgba(0,0,0,.3), 0 0 0 1px ${color}30`; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.04)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.08)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,.2)"; }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 14, background: `${color}18`,
                      border: `1px solid ${color}35`, display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <FontAwesomeIcon icon={icon} style={{ fontSize: 20, color }} />
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,.35)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,.8)", fontWeight: 600 }}>{value}</div>
                    </div>
                  </a>
                ))}
              </div>
            </Reveal>
          </div>
        </section>

        {/* ═══════════════════════════════════════════
            CTA FINAL STRIP
        ═══════════════════════════════════════════ */}
        <section style={{ padding: "clamp(64px, 12vh, 100px) 7vw", background: "#E4B428", position: "relative", overflow: "hidden" }}>
          {/* Decorative big text behind */}
          <div style={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            fontFamily: "'Anton', sans-serif", fontSize: "clamp(80px, 20vw, 160px)",
            color: "rgba(0,0,0,.05)", whiteSpace: "nowrap", userSelect: "none", pointerEvents: "none",
            letterSpacing: 8,
          }}>KARATE</div>

          <div style={{ maxWidth: 800, margin: "0 auto", textAlign: "center", position: "relative" }}>
            <Reveal>
              <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: "clamp(2rem, 7vw, 4rem)", color: "#0d0b1e", textTransform: "uppercase", lineHeight: .92, marginBottom: 16 }}>
                Pronto para<br />Começar?
              </h2>
              <p style={{ fontSize: "clamp(14px, 2vw, 17px)", color: "rgba(13,11,30,.6)", marginBottom: 40, lineHeight: 1.8 }}>
                Junta-te à nossa família e descobre o poder do Karate Shotokan.
              </p>
              <div style={{ display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
                <Link to="/connect/register" style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "15px 40px", background: "#0d0b1e", color: "#fff",
                  borderRadius: 3, fontWeight: 800, fontSize: 13,
                  letterSpacing: 2, textTransform: "uppercase", transition: "all .2s",
                  boxShadow: "0 8px 32px rgba(0,0,0,.25)",
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(0,0,0,.35)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 8px 32px rgba(0,0,0,.25)"; }}>
                  Inscreve-te Já <FontAwesomeIcon icon={faArrowRight} />
                </Link>
                <Link to="/connect/login" style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "15px 36px", background: "transparent", color: "#0d0b1e",
                  borderRadius: 3, fontWeight: 700, fontSize: 13,
                  letterSpacing: 2, textTransform: "uppercase", transition: "all .2s",
                  border: "2px solid rgba(13,11,30,.3)",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(13,11,30,.08)"; e.currentTarget.style.borderColor = "#0d0b1e"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = "rgba(13,11,30,.3)"; }}>
                  AKASB Connect
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      </main>

      {/* ═══════════════════════════════════════════
          FOOTER
      ═══════════════════════════════════════════ */}
      <footer style={{ background: "#080618", padding: "clamp(56px, 10vh, 80px) 7vw 32px" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: "clamp(36px, 6vw, 56px)", marginBottom: 56 }}>
            {/* Brand */}
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <img src={AkasbLogo} alt="AKASB" style={{ height: 34 }} />
                <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 20, color: "#fff", letterSpacing: 3 }}>AKASB</span>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,.3)", lineHeight: 1.9 }}>
                Associação Karate Shotokan<br />de Barcelos.<br />A formar campeões desde 2023.
              </p>
              <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
                {[[faInstagram,"https://www.instagram.com/akasb_karatebarcelos/"],[faFacebook,"https://www.facebook.com/akasbkaratebarcelos/"]].map(([icon, socialHref], i) => (
                  <a key={i} href={socialHref} target="_blank" rel="noreferrer" style={{
                    width: 36, height: 36, borderRadius: 8,
                    background: "rgba(255,255,255,.07)", border: "1px solid rgba(255,255,255,.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    color: "rgba(255,255,255,.5)", transition: "all .2s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#E4B428"; e.currentTarget.style.color = "#0d0b1e"; e.currentTarget.style.borderColor = "#E4B428"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.07)"; e.currentTarget.style.color = "rgba(255,255,255,.5)"; e.currentTarget.style.borderColor = "rgba(255,255,255,.1)"; }}>
                    <FontAwesomeIcon icon={icon} style={{ fontSize: 14 }} />
                  </a>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.3)", letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>Navegação</div>
              {[["#historia","O Clube"],["#instrutores","Equipa Técnica"],["#horario","Horários"],["#localizacao","Localização"],["#contactos","Contactos"]].map(([href, label]) => (
                <HashLink key={href} to={href} style={{ display: "block", fontSize: 13, color: "rgba(255,255,255,.4)", marginBottom: 11, transition: "color .2s" }}
                onMouseEnter={e => e.currentTarget.style.color = "#E4B428"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,.4)"}>
                  {label}
                </HashLink>
              ))}
            </div>

            {/* Contacts */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.3)", letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>Contactos</div>
              {[[faPhone,"(+351) 938 052 610"],[faEnvelope,"geral@akasb.pt"],[faMapMarkerAlt,"Arcozelo, Barcelos"]].map(([icon, val]) => (
                <div key={val} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14, color: "rgba(255,255,255,.4)", fontSize: 13 }}>
                  <FontAwesomeIcon icon={icon} style={{ color: "#E4B428", width: 14 }} /> {val}
                </div>
              ))}
            </div>

            {/* Connect block */}
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,.3)", letterSpacing: 3, textTransform: "uppercase", marginBottom: 20 }}>Plataforma</div>
              <Link to="/connect">
                <div style={{
                  background: "rgba(228,180,40,.08)", border: "1px solid rgba(228,180,40,.2)",
                  borderRadius: 12, padding: "18px 18px",
                  transition: "all .2s",
                }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(228,180,40,.14)"; e.currentTarget.style.borderColor = "rgba(228,180,40,.4)"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "rgba(228,180,40,.08)"; e.currentTarget.style.borderColor = "rgba(228,180,40,.2)"; }}>
                  <div style={{ fontFamily: "'Anton', sans-serif", color: "#E4B428", fontSize: 16, letterSpacing: 2, marginBottom: 6 }}>AKASB Connect</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,.35)", lineHeight: 1.6 }}>
                    Plataforma de gestão para encarregados e direção do clube.
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 11, color: "#E4B428", fontWeight: 700, letterSpacing: 1 }}>
                    Aceder <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 10 }} />
                  </div>
                </div>
              </Link>
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,.06)", paddingTop: 24, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,.18)" }}>
              Copyright © AKASB {new Date().getFullYear()} · Todos os direitos reservados
            </p>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,.18)" }}>
              Feito com ♥ em Barcelos
            </p>
          </div>
        </div>
      </footer>

      {/* Mobile nav CSS fix */}
      <style>{`
        @media (max-width: 900px) {
          .show-mobile-flex { display: flex !important; }
        }
        @media (min-width: 901px) {
          .show-mobile-flex { display: none !important; }
          .hide-mobile { display: flex !important; }
        }
      `}</style>
    </>
  );
}